import { PluginConfigType } from 'Core/PluginCore/PluginsManager'
import { ViolationLevel, ViolationSource } from 'shared/violations/types'
import { Studio } from 'Core/Studio'
import { PlagiarismCheckerComponent } from 'Plugins/Plagiarism/component/PlagiarismChecker'
import { PlagiarismCheckerIcon } from 'Components/common/Icons/PlagiarismCheckerIcon'
import { checkForPlagiarismApi } from 'HTTPClients/plugins.client'
import { UpdatableReactElement, createReactElement } from 'shared/utils/react.utils'
// @ts-ignore
import h2p from 'html2plaintext'
import { isUndefined } from 'lodash'
import { sendAnalyticsTrack } from 'Utils/analytics.utils.ts'
import { convertMarkdownToHTML } from 'Utils/markdown.util'
import { PluginConnector } from 'Core/PluginCore/PluginConnector'
import { BlockStateFormatKey } from 'shared/express-interface/blocks-core/ITaskBlock'
import { BasePlugin } from 'Core/PluginCore/BasePlugin'

export type DisabledStateType = {
    isDisabled: boolean
    reason: string
}

export type PlagiarismPluginConfigType = {
    /**
     * how many times plugin can be used per task
     */
    limit: number
    minWordLimit: number
    maxWordsLimit: number
    /**
     * max % of text plagiarized, above which we wont accept submission
     */
    maximumPercentPlagiarismAllowed: number
    provider: string
    url: string
}

export type PlagiarismSourceType = {
    url: string
    wordsMatched: number
    percentage: number
    textMatched: string[]
}
export type PlagiarismDetailsType = {
    percentage: number
    totalWords: number
    wordsMatched: number
    sourceCount: number
    textMatched: string[]
    sources: PlagiarismSourceType[]
}

export class PlagiarismPlugin extends BasePlugin<PluginConfigType<PlagiarismPluginConfigType>> {
    public readonly id = 'Plagiarism'
    private uiElement?: UpdatableReactElement
    private plagiarismDetails?: PlagiarismDetailsType
    private checksMade: number = 0

    constructor(
        details: PluginConfigType<PlagiarismPluginConfigType>,
        pluginConnectors: PluginConnector[]
    ) {
        super(details, pluginConnectors)
    }

    public getPlagiarismPercentage(): number | undefined {
        return this.plagiarismDetails?.percentage
    }

    public getDetails(): PluginConfigType<PlagiarismPluginConfigType> {
        return this.config
    }

    public async initialize(studio: Studio) {
        this.studio = studio
        studio.workbench?.enqueueUiUpdate(accessor => {
            this.uiElement = createReactElement(PlagiarismCheckerComponent, {
                onCheckForPlagiarism: async () => {
                    this.plagiarismDetails = await this.checkForPlagiarism()
                    if (this.checksMade > 0) {
                        this.removeMandatoryViolation()
                    }
                    this.uiElement?.updater.forceUpdate()
                    if (this.plagiarismDetails) {
                        this.checkViolations(this.plagiarismDetails)
                    }
                },
                getPlagiarismDetails: this.getPlagiarismResponse,
                getDisabledState: this.getDisabledState,
            })
            accessor.rightSidebar.addItem({
                element: this.uiElement.element,
                title: 'Plagiarism Checker',
                icon: PlagiarismCheckerIcon,
                id: this.id,
            })
        })

        const data = this.getSelectedConnector().getData()
        if (data['text']) {
            data['text'].subscribe(() => {
                this.uiElement?.updater.forceUpdate()
            })
        }

        this.addMandatoryViolation()
    }

    public getAnalyticsData(): Record<string, unknown> {
        return {
            plagiarism_percent:
                this.plagiarismDetails?.percentage !== 0
                    ? this.plagiarismDetails?.percentage
                    : 'plagiarsim not enabled',
        }
    }

    public addMandatoryViolation() {
        this.pluginConnectors.forEach(connector => {
            this.studio?.inputManager.addViolation({
                id: `mandatory_plugin_${connector.id}`,
                message: 'You need to run plagiarism check before submitting',
                type: 'error',
                source: ViolationSource.Plugin,
            })
        })
    }

    public removeMandatoryViolation() {
        this.pluginConnectors.forEach(connector => {
            this.studio?.inputManager.removeViolation(`mandatory_plugin_${connector.id}`, 'error')
        })
    }

    public reset() {
        this.plagiarismDetails = undefined
        this.uiElement?.updater.forceUpdate()
    }

    private checkViolations(plagiarismDetails: PlagiarismDetailsType) {
        this.pluginConnectors.forEach(connector => {
            const hasExceededPlagiarismLimit =
                this.config.config.maximumPercentPlagiarismAllowed < plagiarismDetails.percentage

            const level: ViolationLevel = this.hasExceededLimit() ? 'warning' : 'error'

            if (hasExceededPlagiarismLimit) {
                this.studio?.inputManager.removeViolation(`${connector.id}_plagiarism`)
                this.studio?.inputManager.addViolation({
                    id: `${connector.id}_plagiarism`,
                    type: level,
                    message: `Plagiarism percentage (${plagiarismDetails.percentage}%) is higher than the allowed threshold (${this.config.config.maximumPercentPlagiarismAllowed}%)`,
                    source: ViolationSource.Plugin,
                })
            } else {
                this.studio?.inputManager.removeViolation(`${connector.id}_plagiarism`)
            }
        })
    }

    private getDisabledState = (): DisabledStateType => {
        const parametersData = this.getParametersData()
        const data = parametersData['text']

        if (!data?.value) {
            return { isDisabled: true, reason: 'The response is empty' }
        }
        if (this.hasExceededLimit()) {
            return {
                isDisabled: true,
                reason: 'You have reached the maximum limit for plagiarism checks',
            }
        }
        return { isDisabled: false, reason: '' }
    }

    private getPlagiarismResponse = (): PlagiarismDetailsType | undefined => {
        return this.plagiarismDetails
    }

    private hasExceededLimit = (): boolean => {
        return this.checksMade >= this.config.config.limit
    }

    private handleViolation(words: number): boolean {
        if (isUndefined(words)) throw new Error('Words are Undefined')
        if (
            this.config.config?.minWordLimit !== undefined &&
            words < this.config.config?.minWordLimit
        ) {
            this.studio?.workbench.enqueueUiUpdate(ui => {
                ui.notifier.notification.error({
                    message: `Plagiarism Word count is less than ${this.config.config?.minWordLimit} words: ${words}`,
                })
            })
            return true
        } else if (
            this.config.config?.minWordLimit !== undefined &&
            words > this.config.config?.maxWordsLimit
        ) {
            this.studio?.workbench.enqueueUiUpdate(ui => {
                ui.notifier.notification.error({
                    message: `Plagiarism Word count is more than ${this.config.config?.maxWordsLimit} words: ${words}`,
                })
            })
            return true
        } else {
            const connector = this.getSelectedConnector()
            this.studio?.inputManager.removeViolation?.(`${connector.id}_${this.id}`, undefined)
            return false
        }
    }

    private getAnalyticsObject = () => {
        const assignmentObject = this.studio!.taskConfig.assignment
        return {
            project_id: assignmentObject.project_id,
            task_id: assignmentObject.task.id,
            user_id: assignmentObject.user_id,
            step_id: assignmentObject.step?.id,
        }
    }

    private checkForPlagiarism = async (): Promise<PlagiarismDetailsType | undefined> => {
        if (this.hasExceededLimit()) {
            return
        }

        const parametersData = this.getParametersData()
        const data = parametersData['text']

        if (!data?.value) {
            return
        }

        const text = h2p(
            data.key === BlockStateFormatKey.Markdown
                ? convertMarkdownToHTML(data.value as string)
                : data.value
        ) as string
        if (!text) {
            return
        }

        const words = text.split(' ').filter(w => !!w.trim()).length
        this.uiElement?.updater.forceUpdate()
        const hasConfigViolations = this.handleViolation(words)
        if (hasConfigViolations) {
            return
        }

        const requestPayload = {
            taskPluginId: this.config.id,
            text: text,
        }
        sendAnalyticsTrack('Plagiarism_Check_Init', { ...this.getAnalyticsObject() })
        const startTime = +new Date()
        const resp = await checkForPlagiarismApi(requestPayload)
        if (resp.isErr()) {
            sendAnalyticsTrack('Plagiarism_Check_Failure', { ...this.getAnalyticsObject() })
            if (
                (resp.error.response?.data as { error: { message: string } })?.error?.message ===
                'plugin usage limit exhausted.'
            ) {
                this.studio?.workbench.enqueueUiUpdate(ui => {
                    ui.notifier.notification.error({
                        message: 'Limit exceeded',
                        description: 'You have reached the maximum limit for plagiarism checks',
                    })
                })
                this.checksMade = this.config.config.limit
            } else {
                this.studio?.workbench.enqueueUiUpdate(ui => {
                    ui.notifier.notification.error({
                        message: 'Failed to check for plagiarism',
                        description: 'Please try again',
                    })
                })
            }
            return
        }
        const endTime = +new Date()
        sendAnalyticsTrack('Plagiarism_Check_Success', {
            ...this.getAnalyticsObject(),
            plagiarism_percent: resp.value.plag_percent,
            model_response_time: endTime - startTime,
        })
        const plagiarismResponse = resp.value
        const plagiarismDetails: PlagiarismDetailsType = {
            percentage: plagiarismResponse.plag_percent,
            totalWords: plagiarismResponse.total_words,
            wordsMatched: plagiarismResponse.words_matched,
            textMatched: plagiarismResponse.text_matched,
            sourceCount: plagiarismResponse.source_count,
            sources: plagiarismResponse.sources.map(s => ({
                url: s.url,
                wordsMatched: s.words_matched,
                percentage: s.plag_percent,
                textMatched: s.text_matched,
            })),
        }

        this.checksMade++
        return plagiarismDetails
    }
}
